import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Icon, SectionLabel } from "../../kit";
import { RECIPES, recipeThumbDataUrl, recipeThumbUrl, type RingRecipe } from "./recipes";

/**
 * "Start from a design" — the recipe gallery (band designs W4).
 *
 * Every card is a ring set built from ordinary controls; choosing one replaces
 * the current set (the host does it — `RingBuilder.applyRecipe` — as ONE undo
 * step), and every slider stays live afterwards.
 */
export function RecipeGallery({
  busy,
  onApply,
  onClose,
  onStudioLook,
}: {
  busy: boolean;
  onApply: (recipe: RingRecipe) => void;
  onClose: () => void;
  /** v68: shown while a recipe's catalogue look is on — puts the builder's studio light back (one undo step). */
  onStudioLook?: () => void;
}) {
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Start from a design"
      onClick={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-on-surface/40 p-6 backdrop-blur-sm"
    >
      <div
        data-testid="recipe-gallery"
        className="my-auto w-full max-w-[760px] rounded-2xl border border-outline-variant/40 bg-surface-container-lowest p-7 shadow-[0_24px_80px_rgba(0,0,0,0.18)]"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-headline-md text-primary">Start from a design</h2>
            <p className="font-body text-[12px] text-on-surface-variant">
              Each design is made from the builder&rsquo;s own controls, so you can change anything afterwards.
              It replaces the rings you have now; Undo puts them back.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-variant/50"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <SectionLabel>Wedding band pairs</SectionLabel>
          <RecipeCards busy={busy} onApply={onApply} />
        </div>
        {onStudioLook ? (
          <div className="mt-5 flex items-center justify-between gap-3 border-t border-outline-variant/30 pt-4">
            <p className="font-body text-[12px] text-on-surface-variant">
              The rings are shown in the photograph&rsquo;s light. The studio light is one click away.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={onStudioLook}
              className="shrink-0 rounded-full border border-outline-variant/60 px-4 py-1.5 font-body text-[12px] font-medium text-on-surface transition-colors hover:bg-surface-variant/50 disabled:opacity-50"
            >
              Studio look
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/** The cards themselves (exported for the static check — the dialog is a portal). */
export function RecipeCards({ busy, onApply }: { busy: boolean; onApply: (recipe: RingRecipe) => void }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {RECIPES.map((rc) => (
        <RecipeCard key={rc.id} recipe={rc} disabled={busy} onApply={() => onApply(rc)} />
      ))}
    </div>
  );
}

function RecipeCard({ recipe, disabled, onApply }: { recipe: RingRecipe; disabled: boolean; onApply: () => void }) {
  return (
    <button
      type="button"
      data-recipe={recipe.id}
      disabled={disabled}
      onClick={onApply}
      className="group flex flex-col overflow-hidden rounded-xl border border-outline-variant/40 bg-surface text-left transition-colors hover:border-primary disabled:opacity-50"
    >
      <RecipeThumb recipe={recipe} />
      <span className="flex flex-col gap-0.5 px-3 py-2">
        <span className="font-display text-[12px] font-semibold text-primary">{recipe.name}</span>
        <span className="font-body text-[11px] leading-snug text-on-surface-variant">{recipe.blurb}</span>
        <span className="font-body text-[11px] text-outline">
          {recipe.rings.length > 1 ? "Pair" : "Single ring"}
        </span>
      </span>
    </button>
  );
}

/** The rendered webp when it exists, else the recipe's own diagram. */
export function RecipeThumb({ recipe }: { recipe: RingRecipe }) {
  const [failed, setFailed] = useState(false);
  return (
    <img
      src={failed ? recipeThumbDataUrl(recipe) : recipeThumbUrl(recipe.id)}
      alt=""
      width={240}
      height={180}
      loading="lazy"
      onError={() => setFailed(true)}
      className="aspect-[4/3] w-full bg-surface-container-low object-cover"
    />
  );
}
